/**
 * SMELLISH PARFUMQUIZ - Premium Edition
 * Versie 3.0 - Januari 2026
 *
 * Features:
 * - 6 vaste + 2 roterende vragen (8 totaal)
 * - Favorite perfume dropdown (Q4) - boosts similar products
 * - Scent notes visual question (Q5)
 * - Roterende antwoordopties
 * - Login vereist voor resultaat
 * - Geurprofiel generatie
 * - Learning algorithm
 * - Mobile carousel
 * - Fully responsive on all mobile dimensions
 */

const QuizApp = (function() {
  'use strict';

  // ============================================================
  // CONFIGURATION
  // ============================================================

  const API_BASE = (function() {
    const fallback = 'https://smellish-auth.onrender.com';
    try {
      if (typeof window.smellishGetApiBase === 'function') return window.smellishGetApiBase();
      const fromWindow = (window.SMELLISH_API_BASE || '').trim().replace(/\/$/, '');
      if (fromWindow) return fromWindow;
      const meta = document.querySelector('meta[name="smellish-api-base"]');
      if (meta && meta.content) return meta.content.trim().replace(/\/$/, '');
    } catch(_) {}
    return fallback;
  })();

  const CONFIG = {
    totalQuestions: 8,
    fixedQuestions: 6,
    rotatingQuestions: 2,
    recommendationCount: 3,
    storageKey: 'smellish_quiz_data',
    sessionKey: 'smellish_quiz_session'
  };

  // ============================================================
  // DATA POOLS (for rotation)
  // ============================================================

  const DATA = {
    // Geurfamilies - pool of 8, show 4 (all scene-based for consistency)
    geurfamilies: [
      { value: 'Bloemig', label: 'Een bloeiende tuin', desc: 'Rozen, jasmijn, zachte bloemblaadjes', image: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=400&h=400&fit=crop' },
      { value: 'Houtachtig', label: 'Diep in het bos', desc: 'Cederhout, mos, aarde na regen', image: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=400&h=400&fit=crop' },
      { value: 'Fris', label: 'Ochtend op het terras', desc: 'Citrus, groene thee, frisse lucht', image: 'https://images.unsplash.com/photo-1564890369478-c89ca6d9cde9?w=400&h=400&fit=crop' },
      { value: 'Oriëntaals', label: 'Marrakech bij nacht', desc: 'Kruiden, amber, wierook', image: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400&h=400&fit=crop' },
      { value: 'Fruitig', label: 'Zomerse picknick', desc: 'Perzik, frambozen, zonneschijn', image: 'https://images.unsplash.com/photo-1619566636858-adf3ef46400b?w=400&h=400&fit=crop' },
      { value: 'Gourmand', label: 'Parijse patisserie', desc: 'Vanille, karamel, warme gebakjes', image: 'https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=400&h=400&fit=crop' },
      { value: 'Aquatisch', label: 'Ochtend aan zee', desc: 'Zilte bries, schuimend water', image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&h=400&fit=crop' },
      { value: 'Aromatisch', label: 'Mediterrane kruidentuin', desc: 'Lavendel, rozemarijn, tijm', image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&h=400&fit=crop' }
    ],

    // Note: Gelegenheden removed - question not in current quiz flow
    // Data kept for potential future use but not actively used

    // Sfeer/moment - mood-based seasonal alternatives (voor abonnement service)
    seizoenen: [
      { value: 'Lente', label: 'Nieuwe beginnetjes', desc: 'Fris, optimistisch, vol energie', image: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=400&h=400&fit=crop' },
      { value: 'Zomer', label: 'Zonnige vrijheid', desc: 'Licht, zorgeloos, avontuurlijk', image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&h=400&fit=crop' },
      { value: 'Herfst', label: 'Cozy avonden', desc: 'Warm, gezellig, mysterieus', image: 'https://images.unsplash.com/photo-1508193638397-1c4234db14d8?w=400&h=400&fit=crop' },
      { value: 'Winter', label: 'Intieme momenten', desc: 'Rijk, sensueel, verfijnd', image: 'https://images.unsplash.com/photo-1517299321609-52687d1bc55a?w=400&h=400&fit=crop' }
    ],

    // Scent notes - visual question with ingredient images
    scentNotes: [
      { value: 'citrus', label: 'Citrus & Fris', desc: 'Bergamot, citroen, grapefruit', image: 'https://images.unsplash.com/photo-1582979512210-99b6a53386f9?w=400&h=400&fit=crop' },
      { value: 'bloemen', label: 'Bloemen', desc: 'Roos, jasmijn, pioenroos', image: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=400&h=400&fit=crop' },
      { value: 'hout', label: 'Hout & Aarde', desc: 'Cederhout, sandelhout, vetiver', image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=400&fit=crop' },
      { value: 'vanille', label: 'Zoet & Warm', desc: 'Vanille, amber, tonka', image: 'https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=400&h=400&fit=crop' },
      { value: 'kruidig', label: 'Kruiden & Specerijen', desc: 'Peper, kaneel, kardamom', image: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400&h=400&fit=crop' }
    ],

    // Rotating questions pool
    rotatingQuestions: [
      {
        id: 'gevoel',
        title: 'Welke stemming zoek je?',
        subtitle: 'Je parfum versterkt wie je bent',
        isVisual: true,
        options: [
          { value: 'zelfverzekerd', label: 'Zelfverzekerd', desc: 'Klaar om de wereld aan te kunnen', image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=400&fit=crop' },
          { value: 'romantisch', label: 'Romantisch', desc: 'Dromerig en verleidelijk', image: 'https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?w=400&h=400&fit=crop' },
          { value: 'energiek', label: 'Energiek', desc: 'Fris en klaar voor actie', image: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&h=400&fit=crop' },
          { value: 'mysterieus', label: 'Mysterieus', desc: 'Intrigerend en onvergetelijk', image: 'https://images.unsplash.com/photo-1518621736915-f3b1c41bfd00?w=400&h=400&fit=crop' }
        ]
      },
      {
        id: 'dagdeel',
        title: 'Dag of nacht?',
        subtitle: 'Wanneer draag je het meest parfum?',
        options: [
          { value: 'Ochtend', label: 'Overdag', desc: 'Werk, lunch, dagjes uit' },
          { value: 'Avond', label: 's Avonds', desc: 'Dineren, uitgaan, dates' },
          { value: 'Beide', label: 'Allebei', desc: 'Ik wil een allrounder' }
        ]
      },
      {
        id: 'drink',
        title: 'Welk drankje past bij jou?',
        subtitle: 'Smaken zeggen veel over geurvoorkeuren',
        isVisual: true,
        options: [
          { value: 'espresso', label: 'Espresso', desc: 'Krachtig en intens', image: 'https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?w=400&h=400&fit=crop' },
          { value: 'champagne', label: 'Champagne', desc: 'Feestelijk en sprankelend', image: 'https://images.unsplash.com/photo-1578911373434-0cb395d2cbfb?w=400&h=400&fit=crop' },
          { value: 'thee', label: 'Groene thee', desc: 'Rustig en verfijnd', image: 'https://images.unsplash.com/photo-1564890369478-c89ca6d9cde9?w=400&h=400&fit=crop' },
          { value: 'smoothie', label: 'Fruit smoothie', desc: 'Fris en energiek', image: 'https://images.unsplash.com/photo-1505252585461-04db1eb84625?w=400&h=400&fit=crop' }
        ]
      },
      {
        id: 'setting',
        title: 'Je ideale locatie?',
        subtitle: 'Waar voel jij je het prettigst?',
        isVisual: true,
        options: [
          { value: 'stad', label: 'City life', desc: 'Urban en modern', image: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=400&h=400&fit=crop' },
          { value: 'strand', label: 'Beach vibes', desc: 'Relaxed en vrij', image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&h=400&fit=crop' },
          { value: 'natuur', label: 'In de natuur', desc: 'Aards en groen', image: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=400&h=400&fit=crop' },
          { value: 'thuis', label: 'Cozy thuis', desc: 'Intiem en warm', image: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=400&h=400&fit=crop' }
        ]
      },
      {
        id: 'memory',
        title: 'Welke herinnering roept dit op?',
        subtitle: 'Geuren zijn gekoppeld aan emoties',
        isVisual: true,
        options: [
          { value: 'zomer', label: 'Zomervakantie', desc: 'Zon, zee en zorgeloos', image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&h=400&fit=crop' },
          { value: 'kerst', label: 'Kerstmis thuis', desc: 'Warm, gezellig, kaneel', image: 'https://images.unsplash.com/photo-1482517967863-00e15c9b44be?w=400&h=400&fit=crop' },
          { value: 'tuin', label: "Oma's tuin", desc: 'Bloemen en nostalgie', image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&h=400&fit=crop' },
          { value: 'avontuur', label: 'Eerste date', desc: 'Spannend en verleidelijk', image: 'https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?w=400&h=400&fit=crop' }
        ]
      }
    ],

    // Geurprofielen
    profiles: {
      'Bloemig': { name: 'De Romantische Ziel', desc: 'Je wordt aangetrokken door zachte, elegante geuren die je een verfijnd en dromerig gevoel geven.' },
      'Houtachtig': { name: 'De Mysterieuze Avonturier', desc: 'Je houdt van diepgang en warmte. Geuren met karakter die een verhaal vertellen spreken je aan.' },
      'Fris': { name: 'De Energieke Optimist', desc: 'Je kiest voor lichte, opwekkende geuren die je energie geven en fris houden.' },
      'Oriëntaals': { name: 'De Sensuele Kosmopoliet', desc: 'Exotische, rijke geuren met diepte trekken je aan. Je houdt van luxe en verfijning.' },
      'Fruitig': { name: 'De Speelse Dromer', desc: 'Zoete, vrolijke geuren die je humeur opfleuren passen perfect bij jouw uitstraling.' },
      'Gourmand': { name: 'De Warme Genieter', desc: 'Verleidelijke, troostende geuren met zoete accenten maken je gelukkig.' },
      'Aquatisch': { name: 'De Vrije Geest', desc: 'Frisse, oceaanachtige geuren die vrijheid ademen passen bij jouw levenshouding.' },
      'Aromatisch': { name: 'De Verfijnde Kenner', desc: 'Kruidige, elegante geuren met finesse spreken je verfijnde smaak aan.' }
    }
  };

  // ============================================================
  // STATE
  // ============================================================

  const state = {
    currentQuestion: 0,
    answers: {},
    includeUnisex: false,
    rotatingQuestionsSelected: [],
    results: null,
    carouselIndex: 0,
    favoriteProducts: [],        // Loaded from API
    selectedFavorite: null       // Selected favorite product
  };

  const user = {
    isLoggedIn: window.smCustomerLoggedIn || false,
    customerId: window.smCustomerId || null,
    email: window.smCustomerEmail || null,
    name: window.smCustomerName || null,
    hasSubscription: window.smCustomerIsSubscriber === true
  };

  let optionTitleAlignRaf = null;

  // ============================================================
  // INITIALIZATION
  // ============================================================

  function init() {
    // Check if resuming after login
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('resume') === 'true') {
      const savedData = loadFromStorage();

      // Check login status directly from JWT (more reliable than window vars at load time)
      let isLoggedIn = user.isLoggedIn;
      if (!isLoggedIn) {
        try {
          const cached = localStorage.getItem('smellish_customer_jwt');
          if (cached) {
            const parts = cached.split('.');
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1]));
              const exp = payload.exp * 1000;
              const cid = payload.customer_id || (payload.sub && payload.sub.replace('customer:', ''));
              if (cid && exp > Date.now()) {
                isLoggedIn = true;
                // Update user object and window vars for later use
                user.isLoggedIn = true;
                user.customerId = cid;
                window.smCustomerLoggedIn = true;
                window.smCustomerId = cid;
              }
            }
          }
        } catch(e) {
          console.log('[Quiz] JWT check failed:', e);
        }
      }

      if (savedData && isLoggedIn) {
        Object.assign(state, savedData);
        submitQuiz();
        return;
      }
    }

    // Select rotating questions for this session
    selectRotatingQuestions();

    // Populate rotating answer options
    populateRotatingOptions();

    // Load favorite products for dropdown (async)
    loadFavoriteProducts();

    // Populate scent notes question
    populateScentNotes();

    scheduleOptionTitleAlignment();

    // Close mobile dropdown when clicking outside
    document.addEventListener('click', function(e) {
      const dropdown = document.querySelector('.quiz-custom-dropdown');
      if (dropdown && dropdown.classList.contains('open')) {
        if (!dropdown.contains(e.target)) {
          closeMobileDropdown();
        }
      }
    });

    window.addEventListener('resize', scheduleOptionTitleAlignment, { passive: true });
    window.addEventListener('orientationchange', scheduleOptionTitleAlignment);
    window.addEventListener('load', scheduleOptionTitleAlignment);

    console.log('[Quiz] Initialized', { user, rotatingQuestions: state.rotatingQuestionsSelected });
  }

  // Load products for favorite dropdown
  async function loadFavoriteProducts() {
    try {
      console.log('[Quiz] Loading favorite products from API...');
      // Fetch popular/all products from API
      const response = await fetch(`${API_BASE}/quiz/products-for-favorite`);
      if (response.ok) {
        const data = await response.json();
        if (data.ok && data.products && data.products.length > 0) {
          state.favoriteProducts = data.products;
          populateFavoriteDropdown();
          console.log('[Quiz] Successfully loaded', data.products.length, 'products');
        } else {
          console.log('[Quiz] API returned no products, trying fallback...');
          loadFavoriteProductsFallback();
        }
      } else {
        console.log('[Quiz] API request failed with status', response.status);
        loadFavoriteProductsFallback();
      }
    } catch (error) {
      console.log('[Quiz] Error loading favorite products:', error.message);
      loadFavoriteProductsFallback();
    }
  }

  // Fallback: Load products from Shopify's product list if API fails
  function loadFavoriteProductsFallback() {
    // Check if products are available via Shopify's global vars
    if (window.smellishQuizProducts && window.smellishQuizProducts.length > 0) {
      state.favoriteProducts = window.smellishQuizProducts;
      populateFavoriteDropdown();
      console.log('[Quiz] Loaded', state.favoriteProducts.length, 'products from fallback');
    } else {
      console.log('[Quiz] No fallback products available');
    }
  }

  function populateFavoriteDropdown() {
    const selectElement = document.getElementById('favorite-select');
    const mobileOptionsContainer = document.getElementById('favorite-dropdown-options');

    if (state.favoriteProducts.length === 0) return;

    // Filter products by gender based on Q1 answer
    const gender = state.answers.gender; // 'Man' or 'Vrouw'
    let filteredProducts = state.favoriteProducts;

    if (gender && !state.includeUnisex) {
      filteredProducts = state.favoriteProducts.filter(p =>
        p.gender === gender || p.gender === 'Unisex'
      );
    }

    // Group products by brand for better organization
    const productsByBrand = {};
    filteredProducts.forEach(p => {
      const brand = p.brand || 'Overig';
      if (!productsByBrand[brand]) productsByBrand[brand] = [];
      productsByBrand[brand].push(p);
    });

    // Sort brands alphabetically
    const sortedBrands = Object.keys(productsByBrand).sort();

    // Desktop: Native select
    if (selectElement) {
      let optionsHTML = '<option value="">Selecteer een parfum...</option>';
      sortedBrands.forEach(brand => {
        optionsHTML += `<optgroup label="${escapeHtml(brand)}">`;
        productsByBrand[brand].forEach(p => {
          optionsHTML += `<option value="${p.product_id}" data-image="${p.image_url || ''}" data-brand="${escapeHtml(p.brand || '')}">${escapeHtml(p.title)}</option>`;
        });
        optionsHTML += '</optgroup>';
      });
      selectElement.innerHTML = optionsHTML;
    }

    // Mobile: Custom dropdown
    if (mobileOptionsContainer) {
      let mobileHTML = '';
      sortedBrands.forEach(brand => {
        mobileHTML += `<div class="quiz-dropdown-brand">${escapeHtml(brand)}</div>`;
        productsByBrand[brand].forEach(p => {
          mobileHTML += `<button type="button" class="quiz-dropdown-option" data-value="${p.product_id}" data-brand="${escapeHtml(p.brand || '')}" data-title="${escapeHtml(p.title)}" onclick="QuizApp.selectMobileOption('${p.product_id}')">${escapeHtml(p.title)}</button>`;
        });
      });
      mobileOptionsContainer.innerHTML = mobileHTML;
    }

    console.log('[Quiz] Showing', filteredProducts.length, 'of', state.favoriteProducts.length, 'products for favorite dropdown (gender:', gender || 'all', ')');
  }

  // Mobile dropdown functions
  function toggleMobileDropdown() {
    const dropdown = document.querySelector('.quiz-custom-dropdown');
    if (dropdown) {
      dropdown.classList.toggle('open');
      // Don't auto-focus search to prevent keyboard from opening
    }
  }

  function closeMobileDropdown() {
    const dropdown = document.querySelector('.quiz-custom-dropdown');
    if (dropdown) {
      dropdown.classList.remove('open');
    }
  }

  function filterMobileDropdown(query) {
    const optionsContainer = document.getElementById('favorite-dropdown-options');
    if (!optionsContainer) return;

    const normalizedQuery = query.toLowerCase().trim();
    const options = optionsContainer.querySelectorAll('.quiz-dropdown-option');
    const brands = optionsContainer.querySelectorAll('.quiz-dropdown-brand');

    let visibleCount = 0;

    // Track which brands have visible options
    const brandVisibility = {};

    options.forEach(option => {
      const title = (option.dataset.title || '').toLowerCase();
      const brand = (option.dataset.brand || '').toLowerCase();
      const matches = title.includes(normalizedQuery) || brand.includes(normalizedQuery);
      option.style.display = matches ? 'block' : 'none';
      if (matches) {
        visibleCount++;
        brandVisibility[brand] = true;
      }
    });

    // Show/hide brand headers based on visible options
    brands.forEach(brandEl => {
      const brandName = brandEl.textContent.toLowerCase();
      brandEl.style.display = brandVisibility[brandName] ? 'block' : 'none';
    });

    // Show empty message if no results
    let emptyMsg = optionsContainer.querySelector('.quiz-dropdown-empty');
    if (visibleCount === 0) {
      if (!emptyMsg) {
        emptyMsg = document.createElement('div');
        emptyMsg.className = 'quiz-dropdown-empty';
        emptyMsg.textContent = 'Geen parfums gevonden';
        optionsContainer.appendChild(emptyMsg);
      }
      emptyMsg.style.display = 'block';
    } else if (emptyMsg) {
      emptyMsg.style.display = 'none';
    }
  }

  function selectMobileOption(productId) {
    const product = state.favoriteProducts.find(p => p.product_id == productId);
    if (!product) return;

    // Update state
    state.selectedFavorite = product;
    state.answers.favorite = productId;
    state.answers.favoriteData = {
      product_id: product.product_id,
      brand: product.brand,
      geurtype: product.geurtype,
      hoofdakkoorden: product.hoofdakkoorden
    };

    // Update mobile dropdown trigger text
    const triggerText = document.getElementById('favorite-dropdown-text');
    if (triggerText) {
      triggerText.textContent = product.title;
      triggerText.classList.remove('placeholder');
    }

    // Mark selected option
    const options = document.querySelectorAll('.quiz-dropdown-option');
    options.forEach(opt => opt.classList.remove('selected'));
    const selectedOpt = document.querySelector(`.quiz-dropdown-option[data-value="${productId}"]`);
    if (selectedOpt) selectedOpt.classList.add('selected');

    // Close dropdown
    closeMobileDropdown();

    // Clear search
    const searchInput = document.getElementById('favorite-search');
    if (searchInput) searchInput.value = '';
    filterMobileDropdown('');

    // Show preview
    const preview = document.getElementById('favorite-preview');
    if (preview) {
      preview.classList.remove('hidden');
      const img = document.getElementById('favorite-preview-img');
      const title = document.getElementById('favorite-preview-title');
      const brand = document.getElementById('favorite-preview-brand');
      if (img) img.src = product.image_url || '';
      if (title) title.textContent = product.title;
      if (brand) brand.textContent = product.brand || '';
    }

    // Show next button
    const nextBtn = document.getElementById('favorite-next-btn');
    if (nextBtn) nextBtn.style.display = 'flex';
  }

  function selectFavorite(selectElement) {
    const value = selectElement.value;
    const preview = document.getElementById('favorite-preview');
    const nextBtn = document.getElementById('favorite-next-btn');

    if (value === 'skip' || value === '') {
      // User skips or cleared selection - no favorite
      state.selectedFavorite = null;
      state.answers.favorite = value === 'skip' ? 'skip' : null;
      state.answers.favoriteData = null;
      if (preview) preview.classList.add('hidden');
      if (nextBtn) nextBtn.style.display = value === 'skip' ? 'flex' : 'none';
    } else if (value) {
      // User selected a product
      const product = state.favoriteProducts.find(p => p.product_id == value);

      if (product) {
        state.selectedFavorite = product;
        state.answers.favorite = value;
        state.answers.favoriteData = {
          product_id: product.product_id,
          brand: product.brand,
          geurtype: product.geurtype,
          hoofdakkoorden: product.hoofdakkoorden
        };

        // Show preview
        if (preview) {
          preview.classList.remove('hidden');
          const img = document.getElementById('favorite-preview-img');
          const title = document.getElementById('favorite-preview-title');
          const brand = document.getElementById('favorite-preview-brand');

          if (img) img.src = product.image_url || '';
          if (title) title.textContent = product.title;
          if (brand) brand.textContent = product.brand || '';
        }

        // Show next button (user must click to continue)
        if (nextBtn) nextBtn.style.display = 'flex';
      }
    }
  }

  function skipFavorite() {
    state.selectedFavorite = null;
    state.answers.favorite = 'skip';
    state.answers.favoriteData = null;
    next();
  }

  function populateScentNotes() {
    const container = document.getElementById('scentNotes-options');
    if (!container) return;

    const notes = DATA.scentNotes;
    container.innerHTML = notes.map(n => `
      <button class="quiz-option quiz-option-visual" data-value="${n.value}"
              onclick="QuizApp.answer('scentNotes', '${n.value}', this)">
        <div class="quiz-option-image" style="background-image: url('${n.image}')"></div>
        <span class="quiz-option-content">
          <span class="quiz-option-title">${n.label}</span>
          <span class="quiz-option-desc">${n.desc}</span>
        </span>
      </button>
    `).join('');
  }

  function selectRotatingQuestions() {
    // Shuffle and pick 2 rotating questions
    const shuffled = [...DATA.rotatingQuestions].sort(() => Math.random() - 0.5);
    state.rotatingQuestionsSelected = shuffled.slice(0, CONFIG.rotatingQuestions);

    // Populate the rotating question containers (slots 7 and 8 now)
    state.rotatingQuestionsSelected.forEach((q, index) => {
      const slotNum = 7 + index;
      const container = document.getElementById(`rotating-question-${slotNum}`);
      if (container) {
        container.innerHTML = createRotatingQuestionHTML(q, slotNum);
      }
    });
  }

  function createRotatingQuestionHTML(question, slotNum) {
    let optionsHTML = '';

    if (question.isColor) {
      // Color options
      optionsHTML = `<div class="quiz-options quiz-options-colors">`;
      question.options.forEach(opt => {
        optionsHTML += `
          <button class="quiz-option quiz-option-color" data-value="${opt.value}"
                  onclick="QuizApp.answer('${question.id}', '${opt.value}', this)"
                  style="--option-color: ${opt.color}">
            <span class="quiz-color-swatch"></span>
            <span class="quiz-option-content">
              <span class="quiz-option-title">${opt.label}</span>
              <span class="quiz-option-desc">${opt.desc}</span>
            </span>
            <span class="quiz-option-check">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                <path d="M20 6L9 17L4 12"/>
              </svg>
            </span>
          </button>
        `;
      });
      optionsHTML += `</div>`;
    } else if (question.isVisual) {
      // Visual options with images - use same classes as seasons for consistency
      optionsHTML = `<div class="quiz-options quiz-options-visual quiz-options-seasons">`;
      question.options.forEach(opt => {
        optionsHTML += `
          <button class="quiz-option quiz-option-visual quiz-option-season" data-value="${opt.value}"
                  onclick="QuizApp.answer('${question.id}', '${opt.value}', this)">
            <div class="quiz-option-image" style="background-image: url('${opt.image}')"></div>
            <span class="quiz-option-content">
              <span class="quiz-option-title">${opt.label}</span>
              <span class="quiz-option-desc">${opt.desc}</span>
            </span>
          </button>
        `;
      });
      optionsHTML += `</div>`;
    } else {
      // Standard text options
      optionsHTML = `<div class="quiz-options quiz-options-vertical">`;
      question.options.forEach(opt => {
        optionsHTML += `
          <button class="quiz-option quiz-option-descriptive" data-value="${opt.value}"
                  onclick="QuizApp.answer('${question.id}', '${opt.value}', this)">
            <span class="quiz-option-content">
              <span class="quiz-option-title">${opt.label}</span>
              <span class="quiz-option-desc">${opt.desc}</span>
            </span>
            <span class="quiz-option-check">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                <path d="M20 6L9 17L4 12"/>
              </svg>
            </span>
          </button>
        `;
      });
      optionsHTML += `</div>`;
    }

    return `
      <span class="quiz-eyebrow">Vraag ${slotNum}</span>
      <h2 class="quiz-question-title">${question.title}</h2>
      <p class="quiz-question-subtitle">${question.subtitle}</p>
      ${optionsHTML}
      <div class="quiz-nav-bottom">
        <button class="quiz-btn quiz-btn-back" onclick="QuizApp.prev()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M5 12L12 19M5 12L12 5"/>
          </svg>
          <span>Vorige</span>
        </button>
      </div>
    `;
  }

  function populateRotatingOptions() {
    // Geurfamilies - shuffle and show 4 (clean 2x2 layout across mobile/tablet)
    const geurfamilies = shuffleArray([...DATA.geurfamilies]).slice(0, 4);
    const geurfamilieContainer = document.getElementById('geurfamilie-options');
    if (geurfamilieContainer) {
      geurfamilieContainer.innerHTML = geurfamilies.map(g => `
        <button class="quiz-option quiz-option-visual quiz-option-season" data-value="${g.value}"
                onclick="QuizApp.answer('geurfamilie', '${g.value}', this)">
          <div class="quiz-option-image" style="background-image: url('${g.image}')"></div>
          <span class="quiz-option-content">
            <span class="quiz-option-title">${g.label}</span>
            <span class="quiz-option-desc">${g.desc}</span>
          </span>
        </button>
      `).join('');
    }

    // Seizoenen - show all 4 (no shuffle, always show all)
    const seizoenen = DATA.seizoenen;
    const seizoenContainer = document.getElementById('seizoen-options');
    if (seizoenContainer) {
      seizoenContainer.innerHTML = seizoenen.map(s => `
        <button class="quiz-option quiz-option-visual quiz-option-season" data-value="${s.value}"
                onclick="QuizApp.answer('seizoen', '${s.value}', this)">
          <div class="quiz-option-image" style="background-image: url('${s.image}')"></div>
          <span class="quiz-option-content">
            <span class="quiz-option-title">${s.label}</span>
            <span class="quiz-option-desc">${s.desc}</span>
          </span>
        </button>
      `).join('');
    }
  }

  // ============================================================
  // NAVIGATION
  // ============================================================

  function start() {
    showScreen('question', 1);
    showProgress(true);
    updateProgress(1);
  }

  function answer(key, value, element) {
    // Save answer
    state.answers[key] = value;

    // Visual feedback
    const container = element.closest('.quiz-options');
    container.querySelectorAll('.quiz-option').forEach(opt => {
      opt.classList.remove('selected');
    });
    element.classList.add('selected');

    // Auto-advance after short delay
    setTimeout(() => {
      next();
    }, 300);
  }

  // Note: toggleMulti and nextMulti removed - gelegenheid question not in current quiz flow

  function toggleUnisex(checked) {
    state.includeUnisex = checked;
  }

  function next() {
    const currentNum = state.currentQuestion;
    const nextNum = currentNum + 1;

    if (nextNum > CONFIG.totalQuestions) {
      // Quiz completed
      finishQuiz();
    } else {
      showScreen('question', nextNum);
      updateProgress(nextNum);
    }
  }

  function prev() {
    const currentNum = state.currentQuestion;
    if (currentNum > 1) {
      showScreen('question', currentNum - 1);
      updateProgress(currentNum - 1);
    } else {
      // Question 1 -> go back to intro
      showScreen('intro');
      showProgress(false);
      document.body.className = 'quiz-body';
    }
  }

  function finishQuiz() {
    // Save to storage in case user needs to login
    saveToStorage();

    if (user.isLoggedIn) {
      submitQuiz();
    } else {
      // Redirect directly to login page with social choice panel
      // After login, user returns to quiz with resume=true parameter
      showProgress(false);
      const returnUrl = encodeURIComponent('/pages/parfumquiz?resume=true');
      window.location.href = `/account/login?panel=social-choice&return_url=${returnUrl}`;
    }
  }

  // ============================================================
  // SUBMIT & RESULTS
  // ============================================================

  async function submitQuiz() {
    showScreen('loading');
    showProgress(false);

    try {
      // Build submission data
      // Build submission data - only include answers that were actually asked
      const submissionData = {
        answers: {
          // Fixed questions (always asked)
          gender: state.answers.gender,
          geurfamilie: state.answers.geurfamilie,
          seizoen: state.answers.seizoen,
          // Map intensiteit to houdbaarheid/sillage for backend matching
          intensiteit: state.answers.intensiteit,
          houdbaarheid: mapIntensiteit(state.answers.intensiteit, 'houdbaarheid'),
          sillage: mapIntensiteit(state.answers.intensiteit, 'sillage'),
          // Scent notes preference
          scentNotes: state.answers.scentNotes,
          // Favorite perfume (for similarity matching)
          favorite: state.answers.favorite,
          favoriteData: state.answers.favoriteData,
          // Include rotating answers (only 2 are asked per session, others will be undefined)
          gevoel: state.answers.gevoel,
          dagdeel: state.answers.dagdeel,
          drink: state.answers.drink,
          setting: state.answers.setting,
          memory: state.answers.memory,
          // Track which rotating questions were shown this session
          rotatingQuestionsAsked: state.rotatingQuestionsSelected.map(q => q.id)
        },
        include_unisex: state.includeUnisex,
        customer_id: user.customerId,
        session_id: getSessionId()
      };

      console.log('[Quiz] Submitting:', submissionData);

      // Call API
      const response = await fetch(`${API_BASE}/quiz/submit`, {
        method: 'POST',
        headers: await buildAuthHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      console.log('[Quiz] Response:', data);

      if (data.ok && data.recommendations && data.recommendations.length > 0) {
        state.results = data.recommendations;
        state.profileData = data.profile || generateProfile();

        // Clear storage after successful submission
        clearStorage();

        // Set quiz active flags for geurkalender tracking
        localStorage.setItem('quiz_active', 'true');
        localStorage.setItem('quiz_timestamp', Date.now().toString());

        // Wait for smooth transition
        await new Promise(r => setTimeout(r, 1000));

        showResults();
      } else {
        throw new Error('No recommendations found');
      }
    } catch (error) {
      console.error('[Quiz] Submit error:', error);
      showError();
    }
  }

  function mapIntensiteit(intensiteit, type) {
    const mapping = {
      'subtiel': { houdbaarheid: 4, sillage: 3 },
      'merkbaar': { houdbaarheid: 6, sillage: 6 },
      'krachtig': { houdbaarheid: 8, sillage: 9 }
    };
    return mapping[intensiteit]?.[type] || 5;
  }

  function generateProfile() {
    const geurfamilie = state.answers.geurfamilie || 'Bloemig';
    const gevoel = state.answers.gevoel;
    const seizoen = state.answers.seizoen;
    const intensiteit = state.answers.intensiteit;
    const setting = state.answers.setting;

    const baseProfile = DATA.profiles[geurfamilie] || DATA.profiles['Bloemig'];

    // Enhanced descriptions based on multiple answers
    let enhancedDesc = baseProfile.desc;

    // Add personalization based on gevoel (rotating question)
    if (gevoel) {
      const gevoelAdditions = {
        'romantisch': ' Je zoekt geuren die je dromerige, verleidelijke kant versterken.',
        'zelfverzekerd': ' Je kiest parfums die je kracht en uitstraling onderstrepen.',
        'mysterieus': ' Je wordt aangetrokken door geuren met diepte en intriges.',
        'energiek': ' Je houdt van geuren die je dag een positieve boost geven.'
      };
      if (gevoelAdditions[gevoel]) {
        enhancedDesc += gevoelAdditions[gevoel];
      }
    }

    // Add context based on setting (rotating question)
    if (setting) {
      const settingContext = {
        'stad': ' Je houdt van geuren die passen bij je urbane lifestyle.',
        'strand': ' Je zoekt geuren die vrijheid en relaxatie ademen.',
        'natuur': ' Je wordt aangetrokken door authentieke, aardse geuren.',
        'thuis': ' Je kiest geuren die warmte en comfort uitstralen.'
      };
      if (settingContext[setting]) {
        enhancedDesc += settingContext[setting];
      }
    }

    return {
      name: baseProfile.name,
      description: enhancedDesc,
      primaryFamily: geurfamilie
    };
  }

  function showResults() {
    showScreen('results');

    // Clear any auth redirect so navigating away doesn't bounce back
    try {
      sessionStorage.removeItem('smellish_afterAuthRedirect');
    } catch (e) {}

    // Also clear the quiz storage since we're done
    clearStorage();

    // Set profile info
    const profile = state.profileData || generateProfile();
    document.getElementById('quiz-profile-name').textContent = profile.name;
    document.getElementById('quiz-profile-desc').textContent = profile.description;

    // Add social sharing
    renderShareButtons(profile);

    // Render product cards
    const container = document.getElementById('quiz-products-container');
    const products = state.results.slice(0, CONFIG.recommendationCount);

    container.innerHTML = products.map((product, index) => createProductCard(product, index)).join('');

    // Render carousel dots
    const dotsContainer = document.getElementById('quiz-carousel-dots');
    dotsContainer.innerHTML = products.map((_, i) => `
      <button class="quiz-carousel-dot ${i === 0 ? 'active' : ''}" onclick="QuizApp.goToSlide(${i})"></button>
    `).join('');

    // Setup scroll listener for carousel dots sync
    // Setup carousel scroll listener (also marks first card as active)
    setupCarouselScrollListener(container, products.length);

    // Render CTA
    renderCTA();
  }

  // CAROUSEL SCROLL HANDLER - EXACT COPY FROM GEURPROFIEL
  function setupCarouselScrollListener(container, count) {
    if (!container || count <= 1) return;

    let currentIndex = 0;

    // Mark first card as active initially
    const firstCard = container.querySelector('.quiz-product-card');
    if (firstCard) firstCard.classList.add('carousel-active');

    // Scroll handler with center-focus detection - SAME AS GEURPROFIEL
    container.addEventListener('scroll', () => {
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;
      const cards = container.querySelectorAll('.quiz-product-card');

      let closestIndex = 0;
      let closestDistance = Infinity;

      cards.forEach((card, index) => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const distance = Math.abs(containerCenter - cardCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      // Only update when index changes - SAME AS GEURPROFIEL
      if (currentIndex !== closestIndex) {
        currentIndex = closestIndex;
        state.carouselIndex = closestIndex;

        // Update dots
        const dots = document.querySelectorAll('.quiz-carousel-dot');
        dots.forEach((dot, i) => {
          dot.classList.toggle('active', i === closestIndex);
        });

        // Update card focus (center-scale effect)
        cards.forEach((card, i) => {
          card.classList.toggle('carousel-active', i === closestIndex);
        });
      }
    });
  }

  function createProductCard(product, index) {
    const matchBadge = product.match_score >= 70 ? `
      <div class="quiz-product-badge quiz-product-badge-match">
        ${product.match_score}% match
      </div>
    ` : '';

    const rankBadge = index === 0 ? `
      <div class="quiz-product-badge quiz-product-badge-rank">Top keuze</div>
    ` : '';

    // Use data attributes for geurkalender integration
    const title = escapeHtml(product.title);
    const brand = escapeHtml(product.brand || '');
    const img = product.image_url || '';
    const url = product.product_url || '#';
    const variantId = product.variant_id || '';

    // Generate match reason based on user answers
    const matchReason = generateMatchReason(product);

    // Product rank is 1-based for tracking (1, 2, or 3)
    const productRank = index + 1;

    // Show subscription CTA button for non-subscribers
    const subscriptionBtn = !user.hasSubscription ? `
          <button class="quiz-btn quiz-btn-accent quiz-btn-subscribe" onclick="QuizApp.goToCheckout()">
            <span>Start nu voor €9,95</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12H19M19 12L12 5M19 12L12 19"/>
            </svg>
          </button>
          <p class="quiz-subscribe-subtext">Eerste maand €9,95, daarna €15,95/mnd</p>
    ` : '';

    return `
      <div class="quiz-product-card" data-index="${index}">
        ${matchBadge}
        ${rankBadge}
        <a href="${url}" class="quiz-product-image-link" onclick="QuizApp.trackProductClick(${productRank})">
          <img src="${img}" alt="${title}" class="quiz-product-image" loading="lazy">
        </a>
        <div class="quiz-product-content">
          <h3 class="quiz-product-title">${title}</h3>
          <p class="quiz-product-brand">${brand}</p>
          ${product.concentratie ? `<span class="quiz-product-tag">${product.concentratie}</span>` : ''}
          <p class="quiz-product-match-reason">${matchReason}</p>
        </div>
        <div class="quiz-product-actions">
          <button class="fav-add-to-calendar quiz-btn quiz-btn-calendar"
                  data-title="${title}"
                  data-img="${img}"
                  data-brand="${brand}"
                  data-url="${url}"
                  data-variant-id="${variantId}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
              <line x1="12" y1="14" x2="12" y2="18"/>
              <line x1="10" y1="16" x2="14" y2="16"/>
            </svg>
            <span>Voeg toe aan geurkalender</span>
          </button>
          ${subscriptionBtn}
        </div>
      </div>
    `;
  }

  function generateMatchReason(product) {
    // Gather user answers AND product data for smart matching
    const userAnswers = {
      geurfamilie: state.answers.geurfamilie,
      seizoen: state.answers.seizoen,
      intensiteit: state.answers.intensiteit,
      gevoel: state.answers.gevoel,
      dagdeel: state.answers.dagdeel,
      setting: state.answers.setting,
      scentNotes: state.answers.scentNotes
    };

    // Parse product data
    const productGeurtype = (product.geurtype || '').toLowerCase();
    const productGelegenheid = (product.gelegenheid || '').toLowerCase();
    const productHoudbaarheid = product.houdbaarheid || 5;
    const productSillage = product.sillage || 5;

    // Build PRODUCT-SPECIFIC reasons
    const reasons = [];

    // === 1. PRODUCT'S STRONGEST GEURTYPE MATCH ===
    const geurDescriptions = {
      'bloemig': { adj: 'bloemige', desc: 'zachte bloemen' },
      'houtachtig': { adj: 'houtige', desc: 'warme hout' },
      'fris': { adj: 'frisse', desc: 'verfrissende citrus' },
      'oriëntaals': { adj: 'oriëntaalse', desc: 'exotische warmte' },
      'fruitig': { adj: 'fruitige', desc: 'sappig fruit' },
      'gourmand': { adj: 'zoete', desc: 'heerlijke zoetheid' },
      'aquatisch': { adj: 'aquatische', desc: 'frisse zeebries' },
      'musky': { adj: 'sensuele', desc: 'verleidelijke musk' },
      'muskachtig': { adj: 'sensuele', desc: 'verleidelijke musk' },
      'poederig': { adj: 'poederige', desc: 'zachte elegantie' },
      'amber': { adj: 'amberachtige', desc: 'warme amber' },
      'kruidig': { adj: 'kruidige', desc: 'verfijnde kruiden' }
    };

    // Find the primary geurtype of this product
    let primaryGeur = null;
    for (const [key, value] of Object.entries(geurDescriptions)) {
      if (productGeurtype.includes(key)) {
        primaryGeur = value;
        break;
      }
    }

    // === 2. PRODUCT'S GELEGENHEID MATCH (based on product data, not user answer) ===
    const gelegenheidPhrases = {
      'date': 'perfect voor romantische dates',
      'werk': 'ideaal voor op kantoor',
      'avond': 'gemaakt voor mooie avonden',
      'dagelijks': 'heerlijk voor elke dag',
      'feestelijk': 'laat je stralen op feestjes',
      'weekend': 'relaxed voor het weekend',
      'sport': 'fris voor actieve momenten',
      'zomer': 'zomers en zorgeloos',
      'winter': 'warm voor koude dagen'
    };

    // Match product gelegenheid with user's dagdeel preference
    let matchedGelegenheid = null;
    if (userAnswers.dagdeel) {
      // Map dagdeel to relevant product gelegenheden
      const dagdeelToGelegenheid = {
        'Ochtend': ['dagelijks', 'werk', 'sport'],
        'Avond': ['avond', 'date', 'feestelijk'],
        'Beide': ['dagelijks', 'avond', 'weekend']
      };
      const relevantGelegenheden = dagdeelToGelegenheid[userAnswers.dagdeel] || [];
      for (const gel of relevantGelegenheden) {
        if (productGelegenheid.includes(gel)) {
          matchedGelegenheid = gelegenheidPhrases[gel];
          break;
        }
      }
    }

    // === 3. PRODUCT'S INTENSITY MATCH ===
    let intensityMatch = null;
    if (userAnswers.intensiteit === 'krachtig' && productSillage >= 7) {
      intensityMatch = 'krachtig en aanwezig';
    } else if (userAnswers.intensiteit === 'subtiel' && productSillage <= 4) {
      intensityMatch = 'subtiel en verfijnd';
    } else if (userAnswers.intensiteit === 'merkbaar' && productSillage >= 5 && productSillage <= 7) {
      intensityMatch = 'mooi in balans';
    }

    // === 4. PRODUCT'S LONGEVITY HIGHLIGHT ===
    let longevityNote = null;
    if (productHoudbaarheid >= 8) {
      longevityNote = 'blijft de hele dag bij je';
    }

    // === 5. GEVOEL MATCH based on product characteristics ===
    let gevoelMatch = null;
    if (userAnswers.gevoel) {
      const gevoelProductMatch = {
        'romantisch': productGeurtype.includes('bloemig') || productGeurtype.includes('musk') || productGelegenheid.includes('date'),
        'zelfverzekerd': productSillage >= 7 || productGeurtype.includes('hout') || productGeurtype.includes('leer'),
        'mysterieus': productGeurtype.includes('oriëntaals') || productGeurtype.includes('amber') || productGeurtype.includes('musk'),
        'energiek': productGeurtype.includes('fris') || productGeurtype.includes('citrus') || productGeurtype.includes('aqua')
      };

      const gevoelPhrases = {
        'romantisch': 'versterkt je romantische uitstraling',
        'zelfverzekerd': 'geeft je extra zelfvertrouwen',
        'mysterieus': 'maakt je heerlijk mysterieus',
        'energiek': 'geeft je energie'
      };

      if (gevoelProductMatch[userAnswers.gevoel]) {
        gevoelMatch = gevoelPhrases[userAnswers.gevoel];
      }
    }

    // === BUILD FINAL REASON - prioritize most specific matches ===

    // Priority 1: Gevoel match (most personal)
    if (gevoelMatch) {
      reasons.push(gevoelMatch);
    }

    // Priority 2: Gelegenheid match (contextual)
    if (matchedGelegenheid && reasons.length < 1) {
      reasons.push(matchedGelegenheid);
    }

    // Priority 3: Product's character based on geurtype
    if (primaryGeur && reasons.length < 2) {
      reasons.push(`met ${primaryGeur.desc}`);
    }

    // Priority 4: Intensity or longevity (product feature)
    if (intensityMatch && reasons.length < 2) {
      reasons.push(intensityMatch);
    } else if (longevityNote && reasons.length < 2) {
      reasons.push(longevityNote);
    }

    // === FALLBACK ===
    if (reasons.length === 0) {
      if (primaryGeur) {
        return `Een ${primaryGeur.adj} geur die bij je past`;
      }
      return 'Past perfect bij jouw voorkeuren';
    }

    // Combine reasons nicely
    if (reasons.length === 1) {
      // Capitalize first letter
      return reasons[0].charAt(0).toUpperCase() + reasons[0].slice(1);
    }

    // Two parts: capitalize first, keep second lowercase
    const first = reasons[0].charAt(0).toUpperCase() + reasons[0].slice(1);
    return `${first} • ${reasons[1]}`;
  }

  function renderShareButtons(profile) {
    const shareContainer = document.getElementById('quiz-share-section');
    if (!shareContainer) return;

    const shareText = `Ik ben "${profile.name}" 🌸 Ontdek jouw geurprofiel!`;
    const shareUrl = 'https://smellish.nl/pages/parfumquiz';

    // Encode for URLs
    const encodedText = encodeURIComponent(shareText);
    const encodedUrl = encodeURIComponent(shareUrl);

    // Only show share icons, no label text
    shareContainer.innerHTML = `
      <div class="quiz-share-wrapper">
        <div class="quiz-share-buttons">
          <button class="quiz-share-btn quiz-share-whatsapp" onclick="window.open('https://wa.me/?text=${encodedText}%20${encodedUrl}', '_blank')" title="Deel via WhatsApp">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </button>
          <button class="quiz-share-btn quiz-share-instagram" onclick="QuizApp.copyToClipboard(this, '${profile.name}', 'instagram')" title="Kopieer voor Instagram Story">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </button>
          <button class="quiz-share-btn quiz-share-copy" onclick="QuizApp.copyToClipboard(this, '${profile.name}')" title="Kopieer tekst">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  function copyToClipboard(btn, profileName, source) {
    const text = `Ik ben "${profileName}" 🌸 Ontdek jouw geurprofiel op smellish.nl/pages/parfumquiz`;

    // Use modern clipboard API with fallback
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        showCopyFeedback(btn, source);
      }).catch(() => {
        fallbackCopy(text, btn, source);
      });
    } else {
      fallbackCopy(text, btn, source);
    }
  }

  function fallbackCopy(text, btn, source) {
    // Fallback for older browsers/iOS
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.cssText = 'position:fixed;left:-9999px;top:-9999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      document.execCommand('copy');
      showCopyFeedback(btn, source);
    } catch (e) {
      console.error('Copy failed:', e);
      alert('Kopieer deze tekst:\n\n' + text);
    }

    document.body.removeChild(textarea);
  }

  function showCopyFeedback(btn, source) {
    const originalTitle = btn.title;
    btn.title = 'Gekopieerd!';
    btn.classList.add('copied');

    // Different message for Instagram
    const message = source === 'instagram'
      ? '✓ Gekopieerd! Open Instagram en plak in je Story'
      : '✓ Gekopieerd naar klembord!';

    // Show toast notification
    const toast = document.createElement('div');
    toast.className = 'quiz-toast';
    toast.innerHTML = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 80px;
      left: 50%;
      transform: translateX(-50%);
      background: #1a1a1a;
      color: #fff;
      padding: 12px 24px;
      border-radius: 8px;
      font-size: 14px;
      z-index: 9999;
      animation: quiz-toast-in 0.3s ease;
      max-width: 90%;
      text-align: center;
    `;
    document.body.appendChild(toast);

    // Longer display for Instagram message
    const duration = source === 'instagram' ? 3500 : 2000;

    setTimeout(() => {
      btn.title = originalTitle;
      btn.classList.remove('copied');
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, duration);
  }

  function renderCTA() {
    const ctaContainer = document.getElementById('quiz-cta-section');

    // Geurkalender explanation for all users
    const geurkalenderExplain = `
      <p class="quiz-geurkalender-explain">
        Voeg deze geuren toe aan je persoonlijke geurkalender. Wat je invult, ontvang je elke maand thuis: een 8ml twist-up met jouw gekozen originele parfum.
      </p>
    `;

    // Show geurkalender explanation for all users
    // (subscription CTA is now on each product card for non-subscribers)
    ctaContainer.innerHTML = `
      <div class="quiz-cta-subscriber">
        ${geurkalenderExplain}
      </div>
    `;
  }

  function showError() {
    showScreen('results');
    const container = document.getElementById('quiz-products-container');
    container.innerHTML = `
      <div class="quiz-error">
        <h3>Er ging iets mis</h3>
        <p>We konden geen aanbevelingen laden. Probeer het later opnieuw.</p>
        <button class="quiz-btn quiz-btn-primary" onclick="QuizApp.restart()">
          <span>Opnieuw proberen</span>
        </button>
      </div>
    `;
  }

  // ============================================================
  // CAROUSEL (Mobile)
  // ============================================================

  function carouselPrev() {
    const newIndex = Math.max(0, state.carouselIndex - 1);
    goToSlide(newIndex);
  }

  function carouselNext() {
    const maxIndex = CONFIG.recommendationCount - 1;
    const newIndex = Math.min(maxIndex, state.carouselIndex + 1);
    goToSlide(newIndex);
  }

  function goToSlide(index) {
    state.carouselIndex = index;

    // Update carousel position
    const container = document.getElementById('quiz-products-container');
    const cards = container.querySelectorAll('.quiz-product-card');
    cards.forEach((card, i) => {
      card.classList.toggle('carousel-active', i === index);
    });

    // Update dots
    const dots = document.querySelectorAll('.quiz-carousel-dot');
    dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === index);
    });

    // Scroll to card on mobile
    if (window.innerWidth <= 768) {
      const activeCard = cards[index];
      if (activeCard) {
        activeCard.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }

  // ============================================================
  // CALENDAR INTEGRATION
  // ============================================================

  function addToCalendar(title, image, brand, url, variantId) {
    // Store the selected product data
    const productData = { title, image, brand, url, variantId };

    // Dispatch event for smellish geurkalender system
    const event = new CustomEvent('smellish:add-to-calendar', {
      detail: productData
    });
    window.dispatchEvent(event);

    // Also try clicking the geurkalender button if it exists (multiple selectors)
    const selectors = [
      '.geurkalender-knop',
      '.open-geurkalender',
      '.open-geurkalender-account',
      '[data-geurkalender-trigger]'
    ];

    for (const selector of selectors) {
      const btn = document.querySelector(selector);
      if (btn) {
        btn.dataset.title = title;
        btn.dataset.img = image;
        btn.dataset.brand = brand;
        btn.dataset.url = url;
        btn.dataset.variantId = variantId;
        btn.click();
        break;
      }
    }

    // Show feedback to user
    showCalendarFeedback(title);
  }

  // Go to checkout with discount code and monthly plan pre-selected
  function goToCheckout() {
    // Store discount code in localStorage (checkout.js will auto-apply it)
    localStorage.setItem('smellish_discount_code', 'WELKOM26');
    localStorage.setItem('smellish_discount_code_time', Date.now().toString());

    // Store plan preference
    localStorage.setItem('smellish_referral_plan', 'monthly');

    // Redirect to checkout with plan parameter
    window.location.href = '/pages/checkout?plan=monthly';
  }

  function showCalendarFeedback(title) {
    // Create a temporary notification
    const notification = document.createElement('div');
    notification.className = 'quiz-notification';
    notification.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M20 6L9 17L4 12"/>
      </svg>
      <span>${title} toegevoegd aan je lijst</span>
    `;
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: var(--quiz-primary, #1a1a1a);
      color: white;
      padding: 14px 24px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 14px;
      z-index: 9999;
      animation: quiz-notification-in 0.3s ease;
    `;

    // Add animation styles if not present
    if (!document.getElementById('quiz-notification-styles')) {
      const style = document.createElement('style');
      style.id = 'quiz-notification-styles';
      style.textContent = `
        @keyframes quiz-notification-in {
          from { opacity: 0; transform: translateX(-50%) translateY(20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transform = 'translateX(-50%) translateY(20px)';
      setTimeout(() => notification.remove(), 300);
    }, 2500);
  }

  // ============================================================
  // PRODUCT CLICK TRACKING
  // ============================================================

  async function trackProductClick(productRank) {
    try {
      const sessionId = getSessionId();

      console.log('[Quiz] Tracking product click:', { productRank, sessionId });

      const response = await fetch(`${API_BASE}/quiz/track-click`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: sessionId,
          product_rank: productRank
        })
      });

      const result = await response.json();
      console.log('[Quiz] Track click result:', result);
    } catch (error) {
      console.error('[Quiz] Track click failed:', error);
    }
  }

  // ============================================================
  // HELPERS
  // ============================================================

  function showScreen(type, num) {
    document.querySelectorAll('.quiz-screen').forEach(screen => {
      screen.classList.remove('active');
    });

    let targetScreen;
    if (type === 'question') {
      state.currentQuestion = num;
      targetScreen = document.querySelector(`.quiz-screen[data-screen="question"][data-question="${num}"]`);
    } else {
      targetScreen = document.querySelector(`.quiz-screen[data-screen="${type}"]`);
    }

    if (targetScreen) {
      // Re-populate favorite dropdown when navigating to Q4 (applies gender filter)
      if (type === 'question' && num === 4 && state.favoriteProducts.length > 0) {
        populateFavoriteDropdown();
      }

      // Apply background color based on data-bg
      const bg = targetScreen.dataset.bg;
      document.body.className = 'quiz-body';
      if (bg) {
        document.body.classList.add(`quiz-bg-${bg}`);
      }

      setTimeout(() => {
        targetScreen.classList.add('active');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        scheduleOptionTitleAlignment();
      }, 50);
    }
  }

  function scheduleOptionTitleAlignment() {
    if (optionTitleAlignRaf) {
      cancelAnimationFrame(optionTitleAlignRaf);
    }

    optionTitleAlignRaf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        alignOptionTitleHeightsByRow();
      });
    });
  }

  function alignOptionTitleHeightsByRow() {
    const activeQuestions = document.querySelectorAll('.quiz-screen[data-screen="question"].active');
    if (!activeQuestions.length) return;
    const isMobileViewport = window.matchMedia('(max-width: 768px)').matches;

    activeQuestions.forEach(screen => {
      const groups = screen.querySelectorAll('.quiz-options-visual, #scentNotes-options');

      groups.forEach(group => {
        const options = Array.from(group.querySelectorAll('.quiz-option-visual'));
        if (!options.length) return;

        const rows = [];

        options.forEach(option => {
          const title = option.querySelector('.quiz-option-title');
          if (!title) return;

          // Reset previous inline sizing before measuring.
          title.style.minHeight = '';
          title.style.marginBottom = '';

          const top = Math.round(option.getBoundingClientRect().top);
          let row = rows.find(r => Math.abs(r.top - top) <= 4);
          if (!row) {
            row = { top, titles: [] };
            rows.push(row);
          }
          row.titles.push(title);
        });

        rows.forEach(row => {
          let maxHeight = 0;
          let lineHeight = 0;

          row.titles.forEach(title => {
            const rect = title.getBoundingClientRect();
            maxHeight = Math.max(maxHeight, rect.height);

            if (!lineHeight) {
              const parsed = parseFloat(window.getComputedStyle(title).lineHeight);
              if (Number.isFinite(parsed) && parsed > 0) {
                lineHeight = parsed;
              }
            }
          });

          if (!maxHeight) return;

          const rowTitleHeight = Math.ceil(maxHeight);
          const isTwoLineRow = lineHeight > 0 ? maxHeight > lineHeight * 1.35 : maxHeight > 24;
          const rowGap = isTwoLineRow
            ? (isMobileViewport ? 4 : 3)
            : (isMobileViewport ? 2 : 1);

          row.titles.forEach(title => {
            title.style.minHeight = `${rowTitleHeight}px`;
            title.style.marginBottom = `${rowGap}px`;
          });
        });
      });
    });
  }

  function showProgress(show) {
    const progressWrapper = document.getElementById('quiz-progress-wrapper');
    const topHeader = document.getElementById('quiz-top-header');

    if (progressWrapper) {
      progressWrapper.style.display = show ? 'flex' : 'none';
    }
    if (topHeader) {
      topHeader.style.display = show ? 'flex' : 'none';
    }
  }

  function updateProgress(num) {
    const percentage = (num / CONFIG.totalQuestions) * 100;
    const fill = document.querySelector('.quiz-progress-fill');
    const currentText = document.getElementById('quiz-current-q');
    const backBtn = document.getElementById('quiz-header-back-btn');

    if (fill) fill.style.width = `${percentage}%`;
    if (currentText) currentText.textContent = num;

    // Show back button on all questions (goes to intro on Q1, prev question otherwise)
    if (backBtn) {
      backBtn.style.visibility = 'visible';
    }

    // Update navigation dots
    updateNavDots(num);
  }

  function updateNavDots(currentNum) {
    const dots = document.querySelectorAll('.quiz-nav-dot');
    dots.forEach((dot, index) => {
      const questionNum = index + 1;
      dot.classList.remove('active', 'completed');

      if (questionNum === currentNum) {
        // Current question
        dot.classList.add('active');
        dot.disabled = true;
      } else if (questionNum < currentNum) {
        // Completed questions - can click to go back
        dot.classList.add('completed');
        dot.disabled = false;
      } else {
        // Future questions - disabled
        dot.disabled = true;
      }
    });
  }

  function goToQuestion(num) {
    // Only allow going back to completed questions
    if (num < state.currentQuestion && num >= 1) {
      showScreen('question', num);
      updateProgress(num);
    }
  }

  function shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function getSessionId() {
    let sessionId = sessionStorage.getItem(CONFIG.sessionKey);
    if (!sessionId) {
      sessionId = 'quiz_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem(CONFIG.sessionKey, sessionId);
    }
    // Also save to localStorage with key that geurkalender.js expects
    localStorage.setItem('quiz_session_id', sessionId);
    return sessionId;
  }

  // HTML escape helper
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
  }

  async function buildAuthHeaders(initial = {}) {
    const headers = { ...initial };
    if (user.customerId) {
      let token = `customer_${user.customerId}`;
      if (typeof window.getCustomerJWTToken === 'function') {
        token = await window.getCustomerJWTToken(user.customerId);
      }
      headers['X-Shopify-Customer-Token'] = token;
    }
    return headers;
  }

  // ============================================================
  // STORAGE (for login redirect)
  // ============================================================

  function saveToStorage() {
    const data = {
      answers: state.answers,
      includeUnisex: state.includeUnisex,
      rotatingQuestionsSelected: state.rotatingQuestionsSelected,
      timestamp: Date.now()
    };
    localStorage.setItem(CONFIG.storageKey, JSON.stringify(data));
  }

  function loadFromStorage() {
    try {
      const data = localStorage.getItem(CONFIG.storageKey);
      if (!data) return null;

      const parsed = JSON.parse(data);

      // Check if data is less than 30 minutes old
      if (Date.now() - parsed.timestamp > 30 * 60 * 1000) {
        clearStorage();
        return null;
      }

      return parsed;
    } catch (e) {
      return null;
    }
  }

  function clearStorage() {
    localStorage.removeItem(CONFIG.storageKey);
  }

  // ============================================================
  // RESTART
  // ============================================================

  function restart() {
    // Reset state
    state.currentQuestion = 0;
    state.answers = {};
    state.includeUnisex = false;
    state.results = null;
    state.carouselIndex = 0;

    // Clear selected states
    document.querySelectorAll('.quiz-option').forEach(opt => {
      opt.classList.remove('selected');
    });

    // Reset checkbox
    const checkbox = document.getElementById('unisex-checkbox');
    if (checkbox) checkbox.checked = false;

    // Reset next buttons
    const nextBtns = document.querySelectorAll('.quiz-nav-with-next .quiz-btn-primary');
    nextBtns.forEach(btn => btn.disabled = true);

    // Re-shuffle rotating options
    selectRotatingQuestions();
    populateRotatingOptions();

    // Go to intro
    showScreen('intro');
    showProgress(false);
    document.body.className = 'quiz-body';

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ============================================================
  // INIT ON LOAD
  // ============================================================

  document.addEventListener('DOMContentLoaded', init);


  // ============================================================
  // PUBLIC API
  // ============================================================

  function goToIntro() {
    showScreen('intro');
    showProgress(false);
    document.body.className = 'quiz-body';
  }

  return {
    start,
    answer,
    toggleUnisex,
    prev,
    next,
    goToIntro,
    goToQuestion,
    restart,
    carouselPrev,
    carouselNext,
    goToSlide,
    addToCalendar,
    copyToClipboard,
    trackProductClick,
    selectFavorite,
    skipFavorite,
    toggleMobileDropdown,
    filterMobileDropdown,
    selectMobileOption,
    goToCheckout
  };

})();
